import { useLazyQuery } from "@apollo/client";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import ButtonCheckBox from "components/Button-Checkbox";
import Dialog from "components/Dialog";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { GET_TASK_HISTORY } from "domains/Task/graphql/queries";
import { SubTaskStatus, SubTaskType, TaskStatus, TaskType } from "generated/graphql";
import { ExportFormModalProps, ExportItemsParams, IExportTemplate } from "interface/export.interface";
import moment from "moment";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { extractDataToExcel } from "utils";

const useStyles = makeStyles({
  container: {
    paddingBlock: 20,
  },
});

const ExportFormModal: FC<ExportFormModalProps> = ({ open = false, handleClose, clientId }) => {
  const { text } = useText();
  const styles = useStyles();
  const dispatchAlert = useAlertDispatch();
  const [getTaskistory, { data }] = useLazyQuery(GET_TASK_HISTORY, { fetchPolicy: "no-cache" });
  const [isLoading, setIsloading] = useState<boolean>(false);
  const [exportParams, setExportParams] = useState<ExportItemsParams>({
    dateFrom: moment().startOf("month").format("YYYY-MM-DD"),
    clientId,
    dateTo: moment().endOf("month").format("YYYY-MM-DD"),
    taskType: [],
    taskStatus: [],
  });
  useEffect(() => {
    setExportParams({ ...exportParams, clientId });
  }, [clientId]);
  useEffect(() => {
    if (data?.taskToExportClient?.code === 200) {
      if (!data.taskToExportClient.data?.tasksCount || !data.taskToExportClient.data?.tasks?.length) {
        dispatchAlert({
          severity: "warning",
          message: text.all.empty_task,
        });
        setIsloading(false);
      } else {
        extractDataToExcel(
          `task history ${moment().format("YYYY-MM-DD")}`,
          cleanData(data.taskToExportClient.data?.tasks)
        );
        setExportParams({
          dateFrom: moment().startOf("month").format("YYYY-MM-DD"),
          clientId,
          dateTo: moment().endOf("month").format("YYYY-MM-DD"),
          taskType: [],
          taskStatus: [],
        });
        dispatchAlert({
          severity: "success",
          message: text.all.export_success,
        });
        setIsloading(false);
        handleClose();
      }
    }
  }, [data]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (["dateFrom", "dateTo"].includes(e.target.name)) {
      return setExportParams({ ...exportParams, [e.target.name]: moment(e.target.value).format("YYYY-MM-DD") });
    }
    return setExportParams({ ...exportParams, [e.target.name]: e.target.value });
  };

  const dateValidation = () => {
    if (!exportParams.dateFrom || !exportParams.dateTo || moment(exportParams.dateFrom).isAfter(exportParams.dateTo)) {
      dispatchAlert({
        severity: "warning",
        message: text.all.export_date_validation,
      });
      return false;
    }
    return true;
  };

  const confirm = () => {
    if (!dateValidation()) {
      return;
    }
    setIsloading(true);
    getTaskistory({
      variables: {
        queries: {
          ...exportParams,
          taskStatus: !exportParams.taskStatus?.length ? null : exportParams.taskStatus,
          taskType: !exportParams.taskType?.length ? null : exportParams.taskType,
        },
      },
    });
  };

  const changeStatus = (values: string[]) => {
    setExportParams({ ...exportParams, taskStatus: values as TaskStatus[] });
  };
  const changeType = (value: string[]) => {
    setExportParams({ ...exportParams, taskType: value as TaskType[] });
  };

  const cleanData = (data: IExportTemplate[]) =>
    data.map((field) => ({
      [text.all.task_UID]: field.task_UID,
      [text.all.task_type]: field.task_type,
      [text.all.task_status]: field.task_status,
      [text.all.parcels]: field.parcels,
      [text.all.address]: field.address,
      [text.all.city]: field.city,
      [text.all.receiver_fullname]: field.receiver_fullname,
      [text.all.receiver_phone_number]: field.receiver_phone_number,
      [text.all.execution_date]: field.execution_date,
      [text.all.completion_date]: field.completionDate,
      [text.all.creation_date]: field.creation_date,
      [text.all.updated_at]: field.updated_date ? moment(field.updated_date).format("YYYY-MM-DD HH:mm") : "-",
    }));

  return (
    <Dialog
      disableConfirmButton={isLoading}
      maxWidth="md"
      open={open}
      handleClose={handleClose}
      title={text.all.export_task}
      handleConfirm={confirm}
    >
      {isLoading ? (
        <Grid container justifyContent="center" alignItems="center">
          <CircularProgress />
        </Grid>
      ) : (
        <Grid className={styles.container} container spacing={4}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label={text.all.from}
              name="dateFrom"
              variant="outlined"
              onChange={handleChange}
              value={exportParams.dateFrom}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              name="dateTo"
              label={text.all.to_date}
              variant="outlined"
              onChange={handleChange}
              value={exportParams.dateTo}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ButtonCheckBox
              values={Object.values(SubTaskType)}
              titleButton={text.all.task_type}
              allSelectedValues={changeType}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ButtonCheckBox
              values={Object.values(SubTaskStatus)}
              titleButton={text.all.task_status}
              allSelectedValues={changeStatus}
              fullWidth
            />
          </Grid>
        </Grid>
      )}
    </Dialog>
  );
};

export default ExportFormModal;
